import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  MessageSquareText,
  QrCode,
  BarChart3,
  Settings,
  LogOut,
  Sparkles,
  Crown,
  Image,
  Plus,
} from 'lucide-react';
import logoImage from '../assets/logo_small.png';

// App version
const APP_VERSION = 'v1.0.4-stable';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const { business, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isPro = business?.subscriptionTier === 'pro' || business?.subscriptionTier === 'enterprise';

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('sidebar.dashboard') },
    { path: '/reviews', icon: MessageSquareText, label: t('sidebar.reviews') },
    { path: '/qr-codes', icon: QrCode, label: t('sidebar.qrCodes') },
    { path: '/marketing', icon: Image, label: t('sidebar.marketingHub') },
    { path: '/analytics', icon: BarChart3, label: t('sidebar.analytics') },
    { path: '/settings', icon: Settings, label: t('sidebar.settings') },
  ];

  const handleNavClick = () => {
    // Close sidebar on mobile when a nav item is clicked
    onClose();
  };

  return (
    <>
      {/* Sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 text-white flex flex-col
          transform transition-transform duration-300 ease-in-out
          md:relative md:translate-x-0 md:z-auto
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo - Clickable */}
        <div className="p-6 border-b border-gray-800">
          <button
            onClick={() => {
              navigate('/dashboard');
              onClose();
            }}
            className="flex items-center gap-3 w-full text-left hover:opacity-80 transition-opacity"
          >
            <img
              src={logoImage}
              alt="Visto Logo"
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold">Visto</h1>
            </div>
          </button>
        </div>

        {/* Create New QR Button */}
        <div className="p-4">
          <button
            onClick={() => {
              navigate('/qr-codes/new');
              onClose();
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors min-h-[44px] font-semibold"
          >
            <Plus className="w-5 h-5" />
            {t('sidebar.createNewQr')}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors min-h-[44px] ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`
                }
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Upgrade to Pro Button - Only show for free users */}
        {!isPro && (
          <div className="p-4 border-t border-gray-800">
            <button
              onClick={() => {
                navigate('/pricing');
                onClose();
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white rounded-lg transition-all shadow-lg shadow-purple-500/25 min-h-[44px] group"
            >
              <Sparkles className="w-5 h-5 group-hover:animate-pulse" />
              <span className="font-semibold">{t('common.upgradeToPro')}</span>
            </button>
          </div>
        )}

        {/* Business Info */}
        <div className="p-4 border-t border-gray-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shrink-0">
              <span className="font-bold text-white">
                {business?.businessName?.charAt(0) || 'B'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">
                {business?.businessName || 'Business'}
              </p>
              <p className="text-xs text-gray-400 flex items-center gap-1">
                {isPro ? (
                  <>
                    <Crown className="w-3 h-3 text-yellow-400" />
                    <span className="text-yellow-400">{t('common.proPlan')}</span>
                  </>
                ) : (
                  t('common.freePlan')
                )}
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              logout();
              onClose();
            }}
            className="w-full flex items-center gap-2 px-4 py-3 text-gray-300 hover:bg-gray-800 rounded-lg transition-colors min-h-[44px]"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm font-medium">{t('common.signOut')}</span>
          </button>

          {/* Version Number */}
          <p className="text-center text-xs text-gray-600 mt-3">
            {APP_VERSION}
          </p>
        </div>
      </div>
    </>
  );
};
